import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { MediaTile } from "@/components/MediaTile";
import { buildWhatsAppLink, enquireMessage } from "@/lib/whatsapp";
import { Play } from "lucide-react";

/**
 * Premium, near-full-screen gallery viewer. Shows a product/offer's entire
 * media set (cover + gallery photos/videos) with a large main view, a
 * thumbnail strip to jump directly to any item, and a prominent WhatsApp
 * enquiry button — designed to turn a browsing click into an enquiry
 * without the visitor needing to hunt for a button elsewhere on the page.
 */
export const MediaLightbox = ({ item, open, onOpenChange }) => {
  const [api, setApi] = useState(null);
  const [selected, setSelected] = useState(0);

  const mediaItems = React.useMemo(() => {
    if (!item) return [];
    const list = [];
    if (item.media_url) list.push({ url: item.media_url, type: item.media_type || "image" });
    (item.gallery_images || []).forEach((url) => list.push({ url, type: "image" }));
    (item.gallery_videos || []).forEach((url) => list.push({ url, type: "video" }));
    return list;
  }, [item]);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setSelected(api.selectedScrollSnap());
    api.on("select", onSelect);
    onSelect();
    return () => api.off("select", onSelect);
  }, [api]);

  const jumpTo = useCallback((i) => api?.scrollTo(i), [api]);

  if (!item || mediaItems.length === 0) return null;

  const link = buildWhatsAppLink(enquireMessage(item.name || "this item"));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 bg-background border-primary/30 overflow-hidden max-w-none w-[95vw] sm:w-[90vw] lg:w-[80vw] xl:w-[70vw] h-[90vh] flex flex-col"
        data-testid={`media-lightbox-${item.id}`}
      >
        <DialogTitle className="sr-only">{item.name || "Media gallery"}</DialogTitle>
        <DialogDescription className="sr-only">Full media gallery for {item.name}</DialogDescription>

        {/* Main large view */}
        <div className="relative flex-1 min-h-0 bg-black">
          <Carousel setApi={setApi} className="h-full" opts={{ loop: true }}>
            <CarouselContent className="h-full ml-0">
              {mediaItems.map((m, i) => (
                <CarouselItem key={i} className="pl-0 h-full">
                  <div className="relative w-full h-full flex items-center justify-center bg-black">
                    <MediaTile
                      src={m.url}
                      mediaType={m.type}
                      alt={`${item.name} ${i + 1}`}
                      className="max-w-full max-h-full w-auto h-auto object-contain"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {mediaItems.length > 1 && (
              <>
                <CarouselPrevious className="left-3 sm:left-6 h-10 w-10 bg-background/80 hover:bg-background border-primary/40" />
                <CarouselNext className="right-3 sm:right-6 h-10 w-10 bg-background/80 hover:bg-background border-primary/40" />
              </>
            )}
          </Carousel>
        </div>

        {/* Thumbnail strip */}
        {mediaItems.length > 1 && (
          <div className="flex-shrink-0 flex gap-2 overflow-x-auto px-4 py-3 bg-card border-t border-b border-border">
            {mediaItems.map((m, i) => (
              <button
                key={i}
                onClick={() => jumpTo(i)}
                className={`relative flex-shrink-0 h-14 w-14 sm:h-16 sm:w-16 rounded-lg overflow-hidden border-2 transition-all ${
                  selected === i ? "border-primary ring-2 ring-primary/40" : "border-border opacity-70 hover:opacity-100"
                }`}
                data-testid={`media-lightbox-thumb-${i}`}
                aria-label={`View item ${i + 1}`}
              >
                <MediaTile src={m.url} mediaType={m.type} alt="" className="absolute inset-0 w-full h-full object-cover" />
                {m.type === "video" && (
                  <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Play className="h-4 w-4 text-white" fill="white" />
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Info + CTA */}
        <div className="flex-shrink-0 px-5 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1 min-w-0">
            {item.name && <h3 className="font-heading text-xl font-semibold text-foreground truncate">{item.name}</h3>}
            {item.tag && <p className="mt-0.5 text-sm text-muted-foreground truncate">{item.tag}</p>}
            {item.purity && (
              <span className="mt-1.5 inline-block rounded-full bg-primary/15 text-primary text-[11px] font-semibold px-2.5 py-0.5">
                {item.purity}
              </span>
            )}
          </div>
          <Button asChild variant="whatsapp" size="lg" className="w-full sm:w-auto flex-shrink-0" data-testid={`media-lightbox-enquire-btn-${item.id}`}>
            <a href={link} target="_blank" rel="noopener noreferrer">
              <i className="fa-brands fa-whatsapp" /> Enquire on WhatsApp
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

